import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { AdBanner } from '../components/layout/AdBanner';

export default function MockTestEngine() {
  const { user, API } = useAuth();
  const [subject, setSubject] = useState('');
  const [chapter, setChapter] = useState('');
  const [generating, setGenerating] = useState(false);
  const [history, setHistory] = useState([]);
  const navigate = useNavigate();

  const hasKey = !!user?.ai_api_key;

  useEffect(() => {
    fetchHistory();
  }, []);

  const fetchHistory = async () => {
    const { data } = await axios.get(`${API}/tests/list`, { withCredentials: true });
    setHistory(data);
  };

  const handleGenerate = async (e) => {
    e.preventDefault();
    setGenerating(true);
    try {
      const { data } = await axios.post(`${API}/tests/generate`, { subject, chapter }, { withCredentials: true });
      navigate(`/mock-test/${data._id}`);
    } catch (err) {
      alert("Failed to generate test.");
    } finally {
      setGenerating(false);
    }
  };

  return (
    <div className="container py-8 space-y-8" data-testid="mock-test-page">
      <div>
        <h1 className="text-4xl font-heading uppercase text-white mb-2">SIMULATION ENGINE</h1>
        <p className="text-muted-foreground font-bold tracking-widest text-sm uppercase">Generate custom combat scenarios based on your curriculum</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <Card className="lg:col-span-1 border-border rounded-none bg-secondary/30">
          <CardHeader>
            <CardTitle className="font-heading text-2xl tracking-widest text-primary">NEW DEPLOYMENT</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleGenerate} className="space-y-4">
              <div>
                <label className="text-xs font-bold text-muted-foreground uppercase tracking-widest">SUBJECT AREA</label>
                <Input required value={subject} onChange={e => setSubject(e.target.value)} placeholder="E.g. Physics, History" className="mt-1 rounded-none border-border bg-black/50 h-12" data-testid="test-subject" />
              </div>
              <div>
                <label className="text-xs font-bold text-muted-foreground uppercase tracking-widest">CHAPTER / TOPIC (OPTIONAL)</label>
                <Input value={chapter} onChange={e => setChapter(e.target.value)} placeholder="E.g. Thermodynamics" className="mt-1 rounded-none border-border bg-black/50 h-12" data-testid="test-chapter" />
              </div>
              <Button type="submit" disabled={generating || !hasKey} className="w-full h-14 font-heading text-xl tracking-wider rounded-none bg-primary text-white hover:bg-primary/80 disabled:opacity-50" data-testid="test-generate-btn">
                {!hasKey ? 'API KEY REQUIRED' : generating ? 'INITIALIZING AI...' : 'GENERATE SIMULATION'}
              </Button>
            </form>
          </CardContent>
        </Card>

        <div className="lg:col-span-2 space-y-6">
          <AdBanner />
          <h2 className="text-2xl font-heading text-white tracking-widest uppercase">PREVIOUS DEPLOYMENTS</h2>
          <div className="space-y-4">
            {history.map(test => (
              <Card key={test._id} className="rounded-none border-border bg-card flex flex-col md:flex-row items-center justify-between p-4 hover:border-primary/50 transition-all">
                <div className="mb-4 md:mb-0">
                  <div className="font-bold uppercase tracking-widest text-white text-lg">{test.title}</div>
                  <div className="text-xs text-muted-foreground uppercase tracking-widest">{new Date(test.created_at).toLocaleDateString()}</div>
                </div>
                <Button variant="outline" className="rounded-none border-primary text-primary hover:bg-primary hover:text-white" onClick={() => navigate(`/mock-test/${test._id}`)} data-testid={`start-test-${test._id}`}>
                  ENGAGE
                </Button>
              </Card>
            ))}
            {history.length === 0 && <div className="text-center p-8 border border-dashed border-border text-muted-foreground font-bold tracking-widest uppercase">NO PRIOR DEPLOYMENTS</div>}
          </div>
        </div>
      </div>
    </div>
  );
}